import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useAtomValue } from 'jotai';
import { CSSProperties } from 'react';
import { IRoundDataModel } from '../../../data-models/round.data-model';
import { roundsByIdMapAtom, roundsByNameMapAtom } from '../../../services/state/AppConfigStateJ';
import { colors } from '../../../theme/colors';
import { getForesightStore } from '../../../util/jotai-store';
import { isTreeNodeRow } from './GenericIdRenderer';

type PillProps = {
  background: string;
  color: string;
};

const Pill = styled(Typography)<PillProps>`
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ color }) => color};
  background: ${({ background }) => background};
  width: 84%;
  min-width: 2rem;
  height: 1.5rem;
  border-radius: 4px;
`;

// The pill is a centered inline-flex, so the pill's own text-overflow never applies.
// Wrapping the label in this span lets long round names truncate with a trailing ellipsis.
const ELLIPSIS_STYLE: CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  maxWidth: '100%',
};

// Truncate long names with an ellipsis and expose the full name via a native tooltip.
function PillLabel({ name, truncate }: { name: string; truncate?: boolean }) {
  return truncate ? <span style={ELLIPSIS_STYLE}>{name}</span> : <>{name}</>;
}

type RoundRendererProps = {
  value: IRoundDataModel | null | undefined;
  style?: CSSProperties;
  className?: string;
  showTBD?: boolean;
  truncate?: boolean;
};

const ROUND_COLORS = [
  { background: colors.success[20], color: colors.success[70] },
  { background: colors.primary[10], color: colors.primary[60] },
  { background: colors.secondary[10], color: colors.secondary[60] },
  { background: colors.warning[10], color: colors.warning[60] },
  { background: colors.critical[10], color: colors.critical[50] },
];
const TBD_COLORS = { background: colors.neutral[10], color: colors.neutral[70] };

export function getRoundColor(roundId?: number, muted?: boolean): PillProps {
  if (!roundId) {
    return { background: colors.primary[0], color: colors.neutral[40] };
  }
  if (muted) {
    return TBD_COLORS;
  }

  return ROUND_COLORS[roundId % ROUND_COLORS.length];
}

export const RoundRenderer = ({ value, style, className, showTBD, truncate }: RoundRendererProps) => {
  const muted = className === 'muted';
  if ((!value?.name && !showTBD) || value === undefined) {
    return null;
  }
  if (value === null && showTBD) {
    return (
      <Pill {...TBD_COLORS} style={{ ...style }} noWrap>
        TBD
      </Pill>
    );
  }
  const name = value?.name || '-';
  return (
    <Pill
      {...getRoundColor(value?.id, muted)}
      style={{ ...style }}
      noWrap
      title={truncate ? name : undefined}
    >
      <PillLabel name={name} truncate={truncate} />
    </Pill>
  );
};

export function PortfolioRoundIdRenderer(props: CustomCellRendererProps & { style?: CSSProperties }) {
  const id = props.value;
  const roundsById = useAtomValue(roundsByIdMapAtom, { store: getForesightStore() });
  if (!id) {
    return null;
  }
  const round = roundsById.get(id);

  if (props.node.group && !isTreeNodeRow(props)) {
    return <>{props.node.key}</>;
  }

  return <RoundRenderer value={round} style={props.style} />;
}

export function PortfolioRoundNameRenderer(props: CustomCellRendererProps & { style?: CSSProperties }) {
  const name = props.value;
  if (!name) {
    return null;
  }

  if (props.node.group && !isTreeNodeRow(props)) {
    return <>{props.node.key}</>;
  }
  // An aggregated column hands the renderer its aggregator's result object, which has no name.
  if (typeof name !== 'string') {
    return null;
  }

  return <RoundNameRenderer name={name} style={props.style} />;
}

interface IRoundNameRendererParams {
  name: string;
  style?: CSSProperties;
  showTBD?: boolean;
  truncate?: boolean;
}
export function RoundNameRenderer({ name, style, truncate }: IRoundNameRendererParams) {
  const roundsByName = useAtomValue(roundsByNameMapAtom, { store: getForesightStore() });
  const round = roundsByName.get(name);
  if (round) {
    return <RoundRenderer value={round} style={style} truncate={truncate} />;
  }
  const hash = hashCode(name);
  return name.length ? (
    <Pill {...getRoundColor(hash)} style={style} noWrap title={truncate ? name : undefined}>
      <PillLabel name={name} truncate={truncate} />
    </Pill>
  ) : (
    <span />
  );
}

function hashCode(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (str.charCodeAt(i) + 31 * hash) | 0;
  }
  return Math.abs(hash);
}
